import { useCallback, useEffect, useMemo, useState } from 'react';
import { TableDataDto } from '@asset-metrix/portal-gateway-client-js';
import { useDebouncedValue, useElementSize } from '@mantine/hooks';
import {
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelModule,
  ColDef,
  Column,
  ColumnApiModule,
  ColumnAutoSizeModule,
  GridOptions,
  GridReadyEvent,
  GROUP_AUTO_COLUMN_ID,
  Module,
  ModuleRegistry,
  PinnedRowModule,
  ProcessUnpinnedColumnsParams,
  provideGlobalGridOptions,
  ROW_NUMBERS_COLUMN_ID,
  RowApiModule,
  RowAutoHeightModule,
  RowClickedEvent,
  RowSelectionModule,
  SELECTION_COLUMN_ID,
  ValidationModule,
} from 'ag-grid-community';
import { LicenseManager, RowGroupingModule } from 'ag-grid-enterprise';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import clsx from 'clsx';

import { TooltipEllipsis } from '../tooltip/TooltipEllipsis';

import {
  CustomHeader,
  type CustomSortModelItem,
  type ICustomHeaderParams,
} from './custom-components/customHeader';
import { CustomLoadingOverlay, NoRowsOverlay } from './TableOverlays';

import './ag-theme-am-portal-next.scss';

const COMMUNITY_MODULES: Module[] = [
  CellApiModule,
  CellStyleModule,
  ClientSideRowModelModule,
  ColumnApiModule,
  ColumnAutoSizeModule,
  PinnedRowModule,
  RowApiModule,
  RowAutoHeightModule,
  RowSelectionModule,
  ...(process.env.NODE_ENV === 'development' ? [ValidationModule] : []),
];

export const setAgGridLicense = (license: string) => {
  LicenseManager.setLicenseKey(license);
};

const ENTERPRISE_MODULES: Module[] = [RowGroupingModule];

// Register all community ag-grid features
ModuleRegistry.registerModules(COMMUNITY_MODULES);

// Register all enterprise ag-grid features, be aware that a license key is required to enable enterprise features
ModuleRegistry.registerModules(ENTERPRISE_MODULES);

// @todo: migrate scss to new ag-grid Theming API https://www.ag-grid.com/react-data-grid/theming-migration/
provideGlobalGridOptions({ theme: 'legacy' });

const MIN_UNPINNED_VISIBLE_VIEWPORT_WIDTH = 80;

function shouldStayPinned(column: Column<TableDataDto['items'][number]>) {
  return (
    column.getColId() === GROUP_AUTO_COLUMN_ID ||
    column.getColId() === SELECTION_COLUMN_ID ||
    column.getColId() === ROW_NUMBERS_COLUMN_ID
  );
}

function createDefaultGridOptions(
  groupColumnHeaderName?: string,
  groupColumnField?: string,
  groupColumnWidth?: number
): GridOptions {
  return {
    // prevents sorting of grouped tables and pins them - no impact on others
    autoGroupColumnDef: {
      resizable: true,
      sortable: false,
      pinned: 'left',
      field: groupColumnField,
      headerName: groupColumnHeaderName,
      suppressSizeToFit: groupColumnWidth != null,
      width: groupColumnWidth,

      // fix to apply footer title to grouped auto-column only
      valueGetter: (params) => {
        if (
          params.node?.isRowPinned() &&
          params.column?.getColId() === GROUP_AUTO_COLUMN_ID
        ) {
          return params.data?.[GROUP_AUTO_COLUMN_ID];
        }
        return groupColumnField ? params.data?.[groupColumnField] : undefined;
      },
    },
    selectionColumnDef: {
      pinned: 'left',
      maxWidth: 46,
    },
    domLayout: 'autoHeight',
    // https://ag-grid.com/javascript-data-grid/selection-overview/
    enableCellTextSelection: true,
    ensureDomOrder: true,
    // https://www.ag-grid.com/react-data-grid/grid-options/#reference-columnMoving-suppressMovableColumns
    suppressMovableColumns: true,
    // https://ag-grid.com/react-data-grid/column-sizing/#auto-size-columns-to-fit-cell-contents
    suppressColumnVirtualisation: true,
    suppressContextMenu: true,
  };
}

// export own type for easier extensibility
export type TableProps<T> = AgGridReactProps<T> & {
  enableRowSelection?: boolean;
  error?: string;
  expectedRowCount?: number;
  groupColumnField?: string;
  groupColumnHeaderName?: string;
  groupColumnWidth?: number;
  onRowClickedCb?: (
    event: RowClickedEvent<TableDataDto['items'][number]>
  ) => void;
  onSortingChanged: ICustomHeaderParams['onSortingChanged'];
  sortItems?: CustomSortModelItem[];
};

export const Table = (props: TableProps<TableDataDto['items'][number]>) => {
  const {
    columnDefs,
    defaultColDef,
    enableRowSelection,
    error,
    expectedRowCount,
    gridOptions,
    groupColumnField,
    groupColumnHeaderName,
    groupColumnWidth,
    isGroupOpenByDefault,
    loading,
    onColumnResized,
    onRowClickedCb,
    onRowGroupOpened,
    onSelectionChanged,
    onSortingChanged,
    pinnedBottomRowData,
    rowData,
    sortItems,
  } = props;
  const { ref: containerRef, width } = useElementSize();
  const [containerWidth] = useDebouncedValue(width, 100);

  const [gridReadyEvent, setGridReadyEvent] = useState<GridReadyEvent>();

  const components = useMemo<{
    [p: string]: unknown;
  }>(() => {
    return {
      agColumnHeader: CustomHeader,
    };
  }, []);

  const defaultDefaultColDef = useMemo<ColDef>(() => {
    return {
      minWidth: 100,
      maxWidth: 500,
      resizable: false,
      sortable: false,
      wrapHeaderText: true,
      headerComponentParams: {
        onSortingChanged,
        sortItems,
      },
    };
  }, [sortItems, onSortingChanged]);

  // https://ag-grid.com/react-data-grid/column-sizing/#resizing-example
  const autoSizeAllColumns = useCallback(() => {
    if (gridReadyEvent) {
      const { api } = gridReadyEvent;
      // Adapt column size asynchronously to avoid race conditions
      setTimeout(() => {
        // ensure the grid has not been unmounted or otherwise destroyed before making calls
        if (!api.isDestroyed()) {
          const allFlexColumnIds: string[] = [];
          for (const column of api.getAllGridColumns() ?? []) {
            if (column.getColDef().width == null) {
              allFlexColumnIds.push(column.getColId());
            }
          }

          if (allFlexColumnIds.length > 0) {
            api.autoSizeColumns(allFlexColumnIds);
          }

          if (containerWidth > 0) {
            const scrollableWidth =
              api
                .getColumnState()
                ?.filter((c) => !c.hide)
                ?.reduce((width, column) => width + (column.width ?? 0), 0) ??
              0;

            if (scrollableWidth < containerWidth) {
              api.sizeColumnsToFit();
            }
          }
        }
      });
    }
  }, [gridReadyEvent, containerWidth]);

  // listen on grid container width change (browser resize, navigation collapse etc.)
  useEffect(() => {
    autoSizeAllColumns();
  }, [autoSizeAllColumns]);

  // columns changed (table customization)
  useEffect(() => {
    if (columnDefs && columnDefs.length > 0) {
      autoSizeAllColumns();
    }
  }, [autoSizeAllColumns, columnDefs]);

  const onRowClicked = useCallback(
    (event: RowClickedEvent<TableDataDto['items'][number]>) => {
      // prevent ag-grid from triggering onRowClicked events
      // on elements with attribute data-prevent-ag-grid-on-row-clicked=true
      if (
        event.eventPath?.some(
          (el) => (el as HTMLElement)?.dataset?.preventAgGridOnRowClicked
        )
      ) {
        return;
      }
      // make whole group row clickable
      if (event.node.group) {
        event.node.setExpanded(!event.node.expanded);
        return;
      }
      // do not trigger onRowClicked on pinned rows
      if (event.rowPinned) {
        return;
      }
      onRowClickedCb?.(event);
    },
    [onRowClickedCb]
  );

  const processUnpinnedColumns = useCallback(
    (params: ProcessUnpinnedColumnsParams<TableDataDto['items'][number]>) => {
      const allPinnedColumns = params.api.getDisplayedLeftColumns();

      const pinnedColumns = allPinnedColumns.filter(
        (c) => !shouldStayPinned(c)
      );
      const pinnedGroupColumns = allPinnedColumns.filter((c) =>
        shouldStayPinned(c)
      );

      const pinnedGroupColumnsWidth = pinnedGroupColumns.reduce(
        (width, column) => width + column.getActualWidth(),
        0
      );

      let widthOfColumnsSoFar = 0;

      for (const [index, column] of pinnedColumns.entries()) {
        widthOfColumnsSoFar += column.getActualWidth();
        if (
          widthOfColumnsSoFar >
          params.viewportWidth -
            pinnedGroupColumnsWidth -
            MIN_UNPINNED_VISIBLE_VIEWPORT_WIDTH
        ) {
          return pinnedColumns.slice(index - 1);
        }
      }
      return [];
    },
    []
  );

  const onGridReady = (gridReadyEvent: GridReadyEvent) => {
    setGridReadyEvent(gridReadyEvent);
  };

  const defaultGridOptions = useMemo(() => {
    return createDefaultGridOptions(
      groupColumnHeaderName,
      groupColumnField,
      groupColumnWidth
    );
  }, [groupColumnHeaderName, groupColumnField, groupColumnWidth]);

  useEffect(() => {
    if (gridReadyEvent) {
      const { api } = gridReadyEvent;

      api.setGridOption(
        'autoGroupColumnDef',
        defaultGridOptions.autoGroupColumnDef
      );
    }
  }, [defaultGridOptions.autoGroupColumnDef, gridReadyEvent]);

  return (
    <div
      className={clsx(
        'ag-theme-assetmetrix',
        error && 'table--error',
        onRowClickedCb && 'table--enable-row-click'
      )}
      style={{
        // @ts-expect-error css-variable
        '--table-expected-rows': `${
          expectedRowCount && loading && !rowData?.length ? expectedRowCount : 1
        }`,
      }}
    >
      <div className="tooltip-container" ref={containerRef}>
        <AgGridReact
          columnDefs={columnDefs}
          components={components}
          defaultColDef={{ ...defaultDefaultColDef, ...defaultColDef }}
          gridOptions={{
            ...defaultGridOptions,
            ...gridOptions,
          }}
          isGroupOpenByDefault={isGroupOpenByDefault}
          loading={loading || Boolean(error)}
          loadingOverlayComponent={CustomLoadingOverlay}
          loadingOverlayComponentParams={{ error }}
          noRowsOverlayComponent={NoRowsOverlay}
          onColumnResized={onColumnResized}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          onRowGroupOpened={onRowGroupOpened}
          onSelectionChanged={onSelectionChanged}
          pinnedBottomRowData={pinnedBottomRowData}
          processUnpinnedColumns={processUnpinnedColumns}
          rowData={rowData}
          rowSelection={
            enableRowSelection
              ? {
                  hideDisabledCheckboxes: true,
                  isRowSelectable: (rowNode) => (rowNode.group ? false : true),
                  mode: 'multiRow',
                }
              : undefined
          }
          suppressAggFuncInHeader
          suppressPaginationPanel
        ></AgGridReact>
      </div>
      <TooltipEllipsis container={containerRef} />
    </div>
  );
};
